import { periodEnd, periodStart, parseIsoDate } from './format'
import type { DepositStatus, ExpenseCategory } from './types'

/**
 * REGLES DE GESTION, RASSEMBLEES ICI.
 *
 * Elles sont dans un seul fichier, en fonctions pures, pour deux
 * raisons : elles se lisent sans parcourir les ecrans, et aucune ne
 * s'applique toute seule. Chacune produit une SUGGESTION que le
 * proprietaire accepte ou refuse.
 */

// ---------------------------------------------------------------------
// Penalite de retard, Article 3 du bail
// ---------------------------------------------------------------------

export interface PenaltySuggestion {
  daysLate: number
  ratePerDay: number
  base: number
  amount: number
}

/** Taux journalier de l'Article 3, tel que vous me l'avez indique. */
export const PENALTY_RATE_PER_DAY = 0.05

/**
 * Penalite suggeree pour un retard.
 *
 * ATTENTION, DEUX POINTS NE SONT PAS CONFIRMES et sont affiches comme
 * tels a l'ecran :
 *   - la base retenue ici est le SOLDE RESTANT DU, non le loyer entier
 *   - le calcul est simple, non compose, et sans plafond
 * A 5 % par jour, vingt jours de retard sur un loyer de 200 000
 * atteignent le loyer entier : ce n'est jamais applique
 * automatiquement.
 */
export function suggestPenalty(
  period: string,
  balance: number,
  lastPaymentOn: string | null,
  today: Date = new Date(),
): PenaltySuggestion | null {
  if (balance <= 0) return null

  const due = periodStart(period)
  const reference = lastPaymentOn ? parseIsoDate(lastPaymentOn) : today
  if (!reference) return null

  const daysLate = Math.floor(
    (reference.getTime() - due.getTime()) / 86400000,
  )
  if (daysLate <= 0) return null

  return {
    daysLate,
    ratePerDay: PENALTY_RATE_PER_DAY,
    base: balance,
    amount: Math.round(balance * PENALTY_RATE_PER_DAY * daysLate),
  }
}

/** Nombre de jours de retard d'un versement, 0 si dans les delais. */
export function daysLate(period: string, paidOn: string): number {
  const due = periodStart(period)
  const paid = parseIsoDate(paidOn)
  if (!paid) return 0
  return Math.max(0, Math.floor((paid.getTime() - due.getTime()) / 86400000))
}

// ---------------------------------------------------------------------
// Article 3 : reparations a la charge du locataire
// ---------------------------------------------------------------------

/**
 * Mots qui, dans vos baux, designent une reparation a la charge du
 * LOCATAIRE et non du proprietaire. Une depense reperee ainsi n'est pas
 * portee a votre charge par defaut : elle est signalee, et vous
 * tranchez.
 */
const TENANT_CHARGE_WORDS = [
  'wc', 'robinet', 'robinets', 'ampoule', 'ampoules',
  'serrure', 'serrures', 'vidange fosse', 'fosse', 'douche',
]

export function looksLikeTenantCharge(
  description: string,
  category: ExpenseCategory,
): boolean {
  const texte = description.toLowerCase()
  if (TENANT_CHARGE_WORDS.some((mot) => texte.includes(mot))) return true
  // Une vidange de fosse est nommement citee a l'Article 3.
  return category === 'vidange'
}

// ---------------------------------------------------------------------
// Doublons de depenses
// ---------------------------------------------------------------------

export interface DuplicateCandidate {
  id: string
  spent_on: string
  amount: number
  category: ExpenseCategory
  unit_id: string | null
  supplier: string | null
}

/**
 * Doublon possible : meme categorie, meme unite, moins de 30 jours
 * d'ecart, montant proche a 10 % pres.
 *
 * Ce controle existe parce que des travaux ont deja ete payes
 * directement par votre mere tandis que les memes travaux etaient
 * deduits des loyers.
 */
export function findDuplicates(
  candidate: {
    spent_on: string
    amount: number
    category: ExpenseCategory
    unit_id: string | null
  },
  existing: DuplicateCandidate[],
): DuplicateCandidate[] {
  const date = parseIsoDate(candidate.spent_on)
  if (!date) return []

  return existing.filter((e) => {
    if (e.category !== candidate.category) return false
    if ((e.unit_id ?? null) !== (candidate.unit_id ?? null)) return false

    const autre = parseIsoDate(e.spent_on)
    if (!autre) return false
    const jours = Math.abs((date.getTime() - autre.getTime()) / 86400000)
    if (jours > 30) return false

    const ecart = Math.abs(e.amount - candidate.amount)
    const reference = Math.max(e.amount, candidate.amount)
    return reference > 0 && ecart / reference <= 0.1
  })
}

// ---------------------------------------------------------------------
// Appartenance a une annee
// ---------------------------------------------------------------------

/**
 * Ce bail concerne-t-il l'annee demandee ?
 *
 * Meme regle que pour l'ouverture d'un mois, et pour la meme raison :
 * un bail EXPIRE mais toujours occupe concerne les annees suivantes,
 * puisque le locataire est encore la et paie encore. L'ecarter sur sa
 * date de fin ferait disparaitre un locataire reel de l'annee
 * consultee.
 */
export function leaseTouchesYear(
  lease: { start_date: string | null; end_date: string | null; status: string },
  year: number,
): boolean {
  const debut = lease.start_date ? Number(lease.start_date.slice(0, 4)) : null
  const fin = lease.end_date ? Number(lease.end_date.slice(0, 4)) : null

  // Un bail qui commence apres l'annee ne la concerne pas.
  if (debut !== null && debut > year) return false

  // Un bail TERMINE sans aucune date ne peut etre rattache a aucune
  // annee : le rattacher a toutes reviendrait a faire reapparaitre un
  // locataire parti dans chaque annee consultee, y compris celles ou
  // il n'etait pas la. C'est le cas d'un locataire dont les dates ne
  // figurent dans aucun document, marque a verifier. Il reste visible
  // dans l'historique complet de son unite, qui n'est pas filtre par
  // annee : rien n'est perdu, seule l'annee est inconnue.
  if (lease.status === 'termine' && debut === null && fin === null) return false

  // Un bail termine avant l'annee ne la concerne pas. A defaut de date
  // de fin, on se rabat sur l'annee de debut.
  const derniereAnnee = fin ?? (lease.status === 'termine' ? debut : null)
  if (derniereAnnee !== null && derniereAnnee < year && lease.status !== 'expire') {
    return false
  }

  // Un bail expire continue de concerner les annees suivantes tant
  // qu'il n'est pas explicitement termine.
  return true
}

// ---------------------------------------------------------------------
// Ouverture d'un mois
// ---------------------------------------------------------------------

export interface LeaseForCharges {
  id: string
  rent_ttc: number
  advance_month: number
  start_date: string | null
  end_date: string | null
  status: string
}

/**
 * Charges de loyer a creer pour une periode : un bail est concerne si
 * sa duree recouvre la periode.
 *
 * Un bail dont le dernier mois est couvert par le mois d'avance verse a
 * la signature produit une charge marquee comme telle : aucun
 * encaissement n'est attendu, et le mois d'avance n'est donc pas
 * compte deux fois.
 */
export function chargesForPeriod(
  period: string,
  leases: LeaseForCharges[],
): { lease_id: string; period: string; amount_due: number; covered_by_advance: boolean }[] {
  const debut = periodStart(period)
  const fin = periodEnd(period)

  return leases
    .filter((l) => {
      // Seul « termine » signifie que le locataire est parti.
      if (l.status === 'termine') return false

      const ls = l.start_date ? parseIsoDate(l.start_date) : null
      const le = l.end_date ? parseIsoDate(l.end_date) : null

      // Un bail sans date de debut connue est inclus : c'est le cas des
      // baux marques a verifier, et l'omettre creerait un trou dans les
      // loyers attendus.
      if (ls && ls >= fin) return false

      // UN BAIL EXPIRE CONTINUE DE PRODUIRE UN LOYER ATTENDU.
      //
      // « expire » veut dire que le contrat est arrive a echeance mais
      // que le locataire est toujours la et paie toujours : c'est le
      // cas de cinq de vos neuf unites, dont un appartement dont le
      // bail a pris fin en fevrier 2025 et dont le loyer a ete
      // encaisse chaque mois depuis.
      //
      // Ecarter ces baux sur leur date de fin faisait disparaitre cinq
      // loyers du mois : le releve n'aurait montre que quatre unites
      // sur neuf, sans aucune ligne pour enregistrer des versements
      // pourtant reellement recus.
      //
      // Un tel bail produit donc un loyer attendu jusqu'a ce qu'il soit
      // renouvele, ce qui cree un nouveau bail, ou marque termine. La
      // banniere rouge des baux expires reste la pour le rappeler.
      if (le && le < debut && l.status !== 'expire') return false

      return true
    })
    .map((l) => {
      const le = l.end_date ? parseIsoDate(l.end_date) : null
      const dernierMois =
        le !== null && le >= debut && le < fin && l.advance_month > 0
      return {
        lease_id: l.id,
        period,
        amount_due: l.rent_ttc,
        covered_by_advance: dernierMois,
      }
    })
}

// ---------------------------------------------------------------------
// Ce qui se modifie et ce qui se supprime
// ---------------------------------------------------------------------

/**
 * Une caution soldee : l'argent est sorti, plus rien ne bouge.
 *
 * La sortie a exige un approbateur, une piece et l'etat des lieux de
 * sortie, garantis par les contraintes de deposit_movements. Changer le
 * montant apres coup contredirait ces pieces.
 */
export function depositIsSettled(status: DepositStatus): boolean {
  return status === 'rembourse' || status === 'applique'
}

/**
 * Pourquoi cette caution ne peut pas etre supprimee, ou null si elle
 * peut l'etre. Deux verrous, et l'ordre compte : une caution soldee
 * l'est quel que soit le nombre de mouvements, et c'est la raison la
 * plus parlante des deux.
 *
 * Cette fonction dit exactement ce que refusent les declencheurs
 * trg_deposit_settled et trg_deposit_delete de db/012. Elle existe pour
 * que l'ecran et la base ne puissent pas se contredire : sans elle, la
 * regle serait ecrite deux fois, en SQL et en JSX, et une seule des
 * deux serait corrigee le jour ou elle changera.
 */
export function depositDeleteBlock(
  status: DepositStatus,
  movementCount: number,
): 'soldee' | 'mouvements' | null {
  if (depositIsSettled(status)) return 'soldee'
  if (movementCount > 0) return 'mouvements'
  return null
}

/**
 * Pourquoi ce versement en banque ne peut pas etre touche, ou null.
 *
 * Un seul verrou, mais il vaut dans les deux sens : le declencheur
 * trg_lock_bank_deposits est pose sur INSERT, UPDATE **et** DELETE, si
 * bien qu'un versement d'un mois cloture ne se modifie pas plus qu'il
 * ne se supprime.
 */
export function bankDepositBlock(
  period: string,
  closedPeriods: ReadonlySet<string>,
): 'mois_cloture' | null {
  return closedPeriods.has(period) ? 'mois_cloture' : null
}
